import { useEffect } from 'react'

interface WakeLockSentinel {
  readonly released: boolean
  release(): Promise<void>
}

const ignore = () => {
  /* fire-and-forget — failure is non-actionable */
}

/**
 * Hold a screen wake lock for the lifetime of the calling component.
 *
 * Premium poker apps universally do this. Players put the phone face-up
 * on the rail; the screen sleeping every 30s and forcing a re-tap to
 * wake is the kind of friction that quietly degrades the experience.
 *
 * Browser support: Wake Lock API is widely supported on modern Chrome,
 * Edge, Safari 16.4+. We feature-detect — no fallback, no console noise.
 *
 * The lock is automatically released if the tab is hidden (browser
 * contract). On visibility-return we re-acquire it.
 */
export const useWakeLock = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled) return undefined
    // navigator.wakeLock not in stock TS lib yet — narrow cast at the
    // entry point, no `any` leakage past this line.
    const { wakeLock } = navigator as Navigator & {
      wakeLock?: {
        request: (type: 'screen') => Promise<WakeLockSentinel>
      }
    }
    if (!wakeLock?.request) return undefined

    let sentinel: WakeLockSentinel | null = null
    let released = false

    const acquire = async () => {
      try {
        sentinel = await wakeLock.request('screen')
      } catch {
        // Lock can be refused (user gesture required, low battery, etc.)
        // — fail silent. Player keeps using the app; screen may sleep.
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !released && !sentinel) {
        acquire().catch(ignore)
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    acquire().catch(ignore)

    return () => {
      released = true
      document.removeEventListener('visibilitychange', onVisibility)
      sentinel?.release().catch(ignore)
      sentinel = null
    }
  }, [enabled])
}
